/**
 * AttendanceCalendar — Employee and Admin attendance view
 *
 * Employee: monthly calendar view of own attendance + check-in widget
 * Admin/HR: table of all employees with location, device and IP details per record
 *           Click any row → session detail modal with full GPS, browser, IP info
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Table, Badge, Button, Modal,
    Spinner, Form, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
    MapPin, Monitor, Wifi, ChevronLeft, ChevronRight,
    Clock, AlertCircle, ExternalLink, RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import CheckInWidget from '../dashboard/CheckInWidget';

// ── Constants ─────────────────────────────────────────────────────────────────
const HR_ROLES   = ['super-admin','admin','hr-manager','account-manager'];
const MONTH_NAMES= ['Jan','Feb','Mar','Apr','May','Jun',
                    'Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtTime = (t) => {
    if (!t) return '—';
    const [h, m] = String(t).split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ampm}`;
};

const fmtHours = (h) => {
    const v = parseFloat(h || 0);
    if (!v) return '0 min';
    const hrs  = Math.floor(v);
    const mins = Math.round((v - hrs) * 60);
    if (!hrs) return `${mins}m`;
    if (!mins) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN',
        { day:'numeric', month:'short', year:'numeric' });
};

// Google Maps link from lat/lng
const mapsUrl = (lat, lng) =>
    lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;

// ── Location badge ────────────────────────────────────────────────────────────
// source: 'gps' = browser GPS, 'ip' = IP geolocation fallback, null = unknown
const LocationBadge = ({ location, lat, lng, source }) => {
    if (!location && !lat) return <span className="text-muted small">—</span>;
    const url        = mapsUrl(lat, lng);
    const isIP       = !lat && location && location.includes('via IP');
    const iconColor  = isIP ? '#d97706' : '#059669'; // amber for IP, green for GPS
    const iconTitle  = isIP ? 'Approximate location via IP geolocation' : 'GPS location';
    return (
        <div className="d-flex align-items-center gap-1">
            <OverlayTrigger placement="top"
                overlay={<Tooltip>{iconTitle}</Tooltip>}>
                <MapPin size={12} style={{ color: iconColor, flexShrink:0, cursor:'help' }}/>
            </OverlayTrigger>
            <span className="small text-truncate" style={{ maxWidth:160 }}>
                {location || `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`}
            </span>
            {url && (
                <a href={url} target="_blank" rel="noopener noreferrer"
                    className="text-muted" title="Open in Google Maps">
                    <ExternalLink size={11}/>
                </a>
            )}
        </div>
    );
};

// ── Device badge ──────────────────────────────────────────────────────────────
const DeviceBadge = ({ device }) => {
    if (!device) return <span className="text-muted small">—</span>;
    return (
        <div className="d-flex align-items-center gap-1">
            <Monitor size={12} className="text-primary flex-shrink-0"/>
            <span className="small text-truncate" style={{ maxWidth:180 }}>
                {device}
            </span>
        </div>
    );
};

// ── IP badge ──────────────────────────────────────────────────────────────────
const IPBadge = ({ ip }) => {
    if (!ip) return <span className="text-muted small">—</span>;
    return (
        <div className="d-flex align-items-center gap-1">
            <Wifi size={12} className="text-info flex-shrink-0"/>
            <span className="small" style={{ fontFamily:'monospace', fontSize:11 }}>{ip}</span>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const AttendanceCalendar = () => {
    const { user }  = useAuth();
    const isHR      = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState(isHR ? 'admin' : 'my');

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Clock size={22} className="me-2 text-primary"/>Attendance
                    </h4>
                    <p className="text-muted small mb-0">
                        Track attendance, check-in times, and location logs
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button size="sm"
                        variant={tab === 'my' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('my')}>
                        My Attendance
                    </Button>
                    {isHR && (
                        <Button size="sm"
                            variant={tab === 'admin' ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab('admin')}>
                            All Employees
                        </Button>
                    )}
                </div>
            </div>

            {tab === 'my'    && <MyAttendanceTab/>}
            {tab === 'admin' && isHR && <AdminAttendanceTab/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MY ATTENDANCE TAB
// ════════════════════════════════════════════════════════════════════════════
const MyAttendanceTab = () => {
    const now  = new Date();
    const [month,   setMonth]   = useState(now.getMonth() + 1);
    const [year,    setYear]    = useState(now.getFullYear());
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        axiosInstance.get('/attendance/my', { params: { month, year } })
            .then(r => {
                setRecords(r.data.data?.records || []);
                setSummary(r.data.data?.summary  || null);
            })
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    }, [month, year]);

    useEffect(() => { load(); }, [load]);

    const prevMonth = () => {
        if (month === 1) { setMonth(12); setYear(y => y - 1); }
        else setMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (month === 12) { setMonth(1);  setYear(y => y + 1); }
        else setMonth(m => m + 1);
    };

    return (
        <>
            {/* Check-in widget */}
            <CheckInWidget onStatusChange={load}/>

            {/* Month navigator */}
            <div className="d-flex align-items-center gap-3 mb-3">
                <Button size="sm" variant="outline-secondary" onClick={prevMonth}>
                    <ChevronLeft size={16}/>
                </Button>
                <div className="fw-bold">{MONTH_NAMES[month-1]} {year}</div>
                <Button size="sm" variant="outline-secondary" onClick={nextMonth}>
                    <ChevronRight size={16}/>
                </Button>
            </div>

            {/* Summary KPIs */}
            {summary && (
                <Row className="g-3 mb-4">
                    {[
                        { label:'Present',     val:summary.present_days, color:'#059669' },
                        { label:'Absent',      val:summary.absent_days,  color:'#dc2626' },
                        { label:'Total Hours', val:fmtHours(summary.total_hours), color:'#4f46e5' },
                    ].map((k, i) => (
                        <Col key={i} xs={4}>
                            <Card className="border-0 shadow-sm p-3 text-center">
                                <div style={{ fontSize:24, fontWeight:700, color:k.color }}>
                                    {k.val}
                                </div>
                                <div className="text-muted small">{k.label}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Records table */}
            {loading ? (
                <div className="text-center py-4"><Spinner animation="border"/></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                <th className="ps-4">Date</th>
                                <th>Check In</th>
                                <th>Check Out</th>
                                <th>Hours</th>
                                <th>Location</th>
                                <th className="pe-4">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted py-4">
                                    No records for {MONTH_NAMES[month-1]} {year}
                                </td></tr>
                            )}
                            {records.map((r, i) => (
                                <tr key={i}>
                                    <td className="ps-4 fw-medium">
                                        {fmtDate(r.date)}
                                        {r.session_count > 1 && (
                                            <Badge bg="light" text="dark" className="ms-1"
                                                style={{ fontSize:9 }}>
                                                {r.session_count} sessions
                                            </Badge>
                                        )}
                                    </td>
                                    <td>{fmtTime(r.check_in)}</td>
                                    <td>{fmtTime(r.check_out)}</td>
                                    <td className="fw-medium">{fmtHours(r.work_hours)}</td>
                                    <td>
                                        <LocationBadge
                                            location={r.check_in_location}
                                            lat={r.check_in_lat}
                                            lng={r.check_in_lng}
                                        />
                                    </td>
                                    <td className="pe-4">
                                        <Badge bg={r.status==='Present'?'success':'danger'}>
                                            {r.status || 'Present'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// ADMIN ATTENDANCE TAB
// ════════════════════════════════════════════════════════════════════════════
const AdminAttendanceTab = () => {
    const now = new Date();
    const [month,   setMonth]   = useState(now.getMonth() + 1);
    const [year,    setYear]    = useState(now.getFullYear());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail,  setDetail]  = useState(null); // { record } for session modal

    const load = useCallback(() => {
        setLoading(true);
        axiosInstance.get('/attendance', { params: { month, year } })
            .then(r => setRecords(r.data.data || []))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    }, [month, year]);

    useEffect(() => { load(); }, [load]);

    const prevMonth = () => {
        if (month === 1) { setMonth(12); setYear(y => y - 1); }
        else setMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (month === 12) { setMonth(1);  setYear(y => y + 1); }
        else setMonth(m => m + 1);
    };

    // Stats summary
    const totalPresent = records.filter(r => r.status === 'Present').length;
    const totalAbsent  = records.filter(r => r.status === 'Absent').length;
    const withLocation = records.filter(r => r.check_in_lat).length;

    return (
        <>
            {/* Controls */}
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
                <Button size="sm" variant="outline-secondary" onClick={prevMonth}>
                    <ChevronLeft size={16}/>
                </Button>
                <div className="fw-bold" style={{ minWidth:120 }}>
                    {MONTH_NAMES[month-1]} {year}
                </div>
                <Button size="sm" variant="outline-secondary" onClick={nextMonth}>
                    <ChevronRight size={16}/>
                </Button>
                <Button size="sm" variant="outline-primary" onClick={load}>
                    <RefreshCw size={14} className="me-1"/>Refresh
                </Button>
                {/* Quick stats */}
                <div className="ms-auto d-flex gap-2">
                    <Badge bg="success" className="px-3 py-2">{totalPresent} Present</Badge>
                    <Badge bg="danger"  className="px-3 py-2">{totalAbsent} Absent</Badge>
                    <Badge bg="info"    className="px-3 py-2">
                        <MapPin size={11} className="me-1"/>
                        {withLocation} with location
                    </Badge>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border"/></div>
            ) : (
                <Card className="border-0 shadow-sm">
                    <div style={{ overflowX:'auto' }}>
                        <Table hover className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize:10 }}>
                                    <th className="ps-4">Employee</th>
                                    <th>Date</th>
                                    <th>In</th>
                                    <th>Out</th>
                                    <th>Hours</th>
                                    <th>Status</th>
                                    <th>Check-in Location</th>
                                    <th>Device</th>
                                    <th>IP Address</th>
                                    <th className="pe-4 text-center">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.length === 0 && (
                                    <tr>
                                        <td colSpan={10}
                                            className="text-center text-muted py-4">
                                            No records for {MONTH_NAMES[month-1]} {year}
                                        </td>
                                    </tr>
                                )}
                                {records.map((r, i) => (
                                    <tr key={i}>
                                        <td className="ps-4">
                                            <div className="fw-medium">
                                                {r.first_name} {r.last_name}
                                            </div>
                                            <div className="text-muted"
                                                style={{ fontSize:10 }}>
                                                {r.employee_code}
                                            </div>
                                        </td>
                                        <td>
                                            {fmtDate(r.date)}
                                            {r.session_count > 1 && (
                                                <div>
                                                    <Badge bg="light" text="dark"
                                                        style={{ fontSize:9 }}>
                                                        {r.session_count}× sessions
                                                    </Badge>
                                                </div>
                                            )}
                                        </td>
                                        <td>{fmtTime(r.check_in)}</td>
                                        <td>{fmtTime(r.check_out)}</td>
                                        <td className="fw-medium">{fmtHours(r.work_hours)}</td>
                                        <td>
                                            <Badge bg={r.status==='Present'?'success':'danger'}>
                                                {r.status||'Present'}
                                            </Badge>
                                        </td>
                                        <td style={{ minWidth:180 }}>
                                            <LocationBadge
                                                location={r.check_in_location}
                                                lat={r.check_in_lat}
                                                lng={r.check_in_lng}
                                            />
                                        </td>
                                        <td style={{ minWidth:180 }}>
                                            <DeviceBadge device={r.check_in_device}/>
                                        </td>
                                        <td>
                                            <IPBadge ip={r.check_in_ip}/>
                                        </td>
                                        <td className="pe-4 text-center">
                                            <Button size="sm"
                                                variant="outline-primary"
                                                className="py-0 px-2"
                                                onClick={() => setDetail(r)}>
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* Session detail modal */}
            {detail && (
                <SessionDetailModal
                    record={detail}
                    onClose={() => setDetail(null)}
                />
            )}
        </>
    );
};

// ── Session Detail Modal ──────────────────────────────────────────────────────
const SessionDetailModal = ({ record, onClose }) => {
    const [sessions, setSessions] = useState([]);
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        axiosInstance.get('/attendance/sessions', {
            params: {
                employee_id: record.employee_id,
                date:        record.date,
            }
        })
        .then(r => setSessions(r.data.data || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, [record]);

    const name = `${record.first_name} ${record.last_name}`;

    const InfoRow = ({ icon: Icon, label, value, link }) => (
        <div className="d-flex align-items-start gap-2 mb-3">
            <div className="rounded p-2 flex-shrink-0"
                style={{ background:'#f1f5f9' }}>
                <Icon size={14} className="text-muted"/>
            </div>
            <div>
                <div className="text-muted" style={{ fontSize:11 }}>{label}</div>
                <div className="fw-medium" style={{ fontSize:13 }}>
                    {link
                        ? <a href={link} target="_blank" rel="noopener noreferrer"
                            className="text-primary">
                            {value} <ExternalLink size={11} className="ms-1"/>
                          </a>
                        : value || '—'}
                </div>
            </div>
        </div>
    );

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fs-6 fw-bold">
                    Attendance Detail — {name}
                    <div className="text-muted fw-normal" style={{ fontSize:13 }}>
                        {fmtDate(record.date)}
                        {record.employee_code && ` · ${record.employee_code}`}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4"><Spinner animation="border"/></div>
                ) : sessions.length === 0 ? (
                    // Fallback to record data if sessions API returns nothing
                    <SessionCard session={record} index={0} isOnly/>
                ) : (
                    sessions.map((s, i) => (
                        <SessionCard key={s.id} session={s} index={i}
                            isOnly={sessions.length === 1}/>
                    ))
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

// ── Individual session card inside modal ──────────────────────────────────────
const SessionCard = ({ session: s, index, isOnly }) => {
    const label = isOnly ? 'Session Details' : `Session ${index + 1}`;

    const inMaps  = mapsUrl(s.check_in_lat,  s.check_in_lng);
    const outMaps = mapsUrl(s.check_out_lat, s.check_out_lng);

    return (
        <Card className="border-0 shadow-sm mb-3 p-4">
            <div className="fw-bold mb-3" style={{ fontSize:14 }}>
                {label}
                {s.check_in && (
                    <span className="text-muted fw-normal ms-2" style={{ fontSize:12 }}>
                        {fmtTime(s.check_in)}
                        {s.check_out ? ` → ${fmtTime(s.check_out)}` : ' → In progress'}
                        {s.work_hours ? ` · ${fmtHours(s.work_hours)}` : ''}
                    </span>
                )}
            </div>

            <Row className="g-0">
                {/* Check-in column */}
                <Col xs={12} md={6} className="pe-md-4">
                    <div className="text-success fw-bold small mb-2">
                        ↳ Check-in
                    </div>
                    <div className="mb-2">
                        <div className="text-muted" style={{ fontSize:11 }}>Time</div>
                        <div className="fw-medium">{fmtTime(s.check_in) || '—'}</div>
                    </div>
                    <div className="mb-2">
                        <div className="text-muted d-flex align-items-center gap-1"
                            style={{ fontSize:11 }}>
                            <MapPin size={11}/> Location
                        </div>
                        <div className="fw-medium" style={{ fontSize:13 }}>
                            {s.check_in_location || '—'}
                        </div>
                        {s.check_in_lat && (
                            <div className="text-muted" style={{ fontSize:11 }}>
                                {parseFloat(s.check_in_lat).toFixed(5)},
                                {parseFloat(s.check_in_lng).toFixed(5)}
                                {s.check_in_accuracy && ` ±${Math.round(s.check_in_accuracy)}m`}
                            </div>
                        )}
                        {inMaps && (
                            <a href={inMaps} target="_blank" rel="noopener noreferrer"
                                className="small text-primary">
                                Open in Google Maps <ExternalLink size={10}/>
                            </a>
                        )}
                    </div>
                    <div className="mb-2">
                        <div className="text-muted d-flex align-items-center gap-1"
                            style={{ fontSize:11 }}>
                            <Monitor size={11}/> Device
                        </div>
                        <div style={{ fontSize:12 }}>{s.check_in_device || '—'}</div>
                    </div>
                    <div>
                        <div className="text-muted d-flex align-items-center gap-1"
                            style={{ fontSize:11 }}>
                            <Wifi size={11}/> IP Address
                        </div>
                        <div style={{ fontFamily:'monospace', fontSize:12 }}>
                            {s.check_in_ip || '—'}
                        </div>
                    </div>
                </Col>

                {/* Divider */}
                <Col xs={12} md="auto" className="d-none d-md-block">
                    <div style={{ width:1, background:'#e5e7eb', height:'100%' }}/>
                </Col>
                <hr className="d-md-none my-3"/>

                {/* Check-out column */}
                <Col xs={12} md className="ps-md-4">
                    <div className="text-primary fw-bold small mb-2">
                        ↲ Check-out
                    </div>
                    {!s.check_out ? (
                        <div className="text-muted small d-flex align-items-center gap-1">
                            <AlertCircle size={13} className="text-warning"/>
                            Session still open
                        </div>
                    ) : (
                        <>
                            <div className="mb-2">
                                <div className="text-muted" style={{ fontSize:11 }}>Time</div>
                                <div className="fw-medium">{fmtTime(s.check_out)}</div>
                            </div>
                            <div className="mb-2">
                                <div className="text-muted d-flex align-items-center gap-1"
                                    style={{ fontSize:11 }}>
                                    <MapPin size={11}/> Location
                                </div>
                                <div className="fw-medium" style={{ fontSize:13 }}>
                                    {s.check_out_location || '—'}
                                </div>
                                {s.check_out_lat && (
                                    <div className="text-muted" style={{ fontSize:11 }}>
                                        {parseFloat(s.check_out_lat).toFixed(5)},
                                        {parseFloat(s.check_out_lng).toFixed(5)}
                                        {s.check_out_accuracy &&
                                            ` ±${Math.round(s.check_out_accuracy)}m`}
                                    </div>
                                )}
                                {outMaps && (
                                    <a href={outMaps} target="_blank"
                                        rel="noopener noreferrer"
                                        className="small text-primary">
                                        Open in Google Maps <ExternalLink size={10}/>
                                    </a>
                                )}
                            </div>
                            <div className="mb-2">
                                <div className="text-muted d-flex align-items-center gap-1"
                                    style={{ fontSize:11 }}>
                                    <Monitor size={11}/> Device
                                </div>
                                <div style={{ fontSize:12 }}>
                                    {s.check_out_device || '—'}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted d-flex align-items-center gap-1"
                                    style={{ fontSize:11 }}>
                                    <Wifi size={11}/> IP Address
                                </div>
                                <div style={{ fontFamily:'monospace', fontSize:12 }}>
                                    {s.check_out_ip || '—'}
                                </div>
                            </div>
                        </>
                    )}
                </Col>
            </Row>
        </Card>
    );
};

export default AttendanceCalendar;
